import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { getSubscriptionStatus } from "../api/subscriptionClient";
import { useAuth } from "./AuthContext";

const SubscriptionContext = createContext({
    subscription: null,
    loading: true,
    hasActiveSubscription: false,
    error: null,
    refreshSubscription: () => {},
});

export function SubscriptionProvider({ children }) {
    const { isAuthenticated, loading: authLoading } = useAuth();
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const initialLoadDone = useRef(false);

    const refreshSubscription = useCallback(async () => {
        // Wait for auth to finish loading before checking subscription
        if (authLoading) {
            return;
        }

        if (!isAuthenticated) {
            setSubscription(null);
            setLoading(false);
            setError(null);
            initialLoadDone.current = false;
            return;
        }

        // Only show loading spinner on initial load, not on refreshes.
        // Setting loading=true on refresh causes ProtectedRoute to unmount
        // children (e.g. SubscriptionSuccess polling loop).
        if (!initialLoadDone.current) {
            setLoading(true);
        }
        setError(null);

        try {
            const status = await getSubscriptionStatus();
            setSubscription(status);
        } catch (err) {
            console.error("Failed to fetch subscription status:", err);
            // Don't set subscription to null on error - keep previous state
            // This prevents redirecting to /subscribe on transient errors
            setError(err);
        } finally {
            setLoading(false);
            initialLoadDone.current = true;
        }
    }, [isAuthenticated, authLoading]);

    useEffect(() => {
        refreshSubscription();
    }, [refreshSubscription]);

    const hasActiveSubscription = subscription?.isActive ?? false;

    const value = {
        subscription,
        loading: loading || authLoading,
        hasActiveSubscription,
        error,
        refreshSubscription,
    };

    return (
        <SubscriptionContext.Provider value={value}>
            {children}
        </SubscriptionContext.Provider>
    );
}

export function useSubscription() {
    const context = useContext(SubscriptionContext);
    if (!context) {
        throw new Error("useSubscription must be used within a SubscriptionProvider");
    }
    return context;
}
